import { AudioRegionBox } from "@opendaw/studio-boxes";
import { Notifier, Option, safeExecute, Terminator, UUID } from "@opendaw/lib-std";
import { Propagation } from "@opendaw/lib-box";
import { PPQN } from "@opendaw/lib-dsp";
import { TrackBoxAdapter } from "../TrackBoxAdapter";
import { AudioFileBoxAdapter } from "../../audio/AudioFileBoxAdapter";
export class AudioRegionBoxAdapter {
    type = "audio-region";
    #terminator;
    #context;
    #box;
    #changeNotifier;
    #fileAdapter = Option.None;
    #fileSubscription = Option.None;
    #isSelected;
    #constructing;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#terminator = new Terminator();
        this.#changeNotifier = new Notifier();
        this.#isSelected = false;
        this.#constructing = true;
        // TODO For unsyned audio samples
        // this.#terminator.own(this.#project.timelineBox.bpm.subscribe(() => this.trackAdapter.unwrapOrNull()?.dispatchChange()))
        this.#terminator.ownAll(this.#box.pointerHub.subscribe({
            onAdded: () => this.#dispatchChange(),
            onRemoved: () => this.#dispatchChange()
        }), this.#box.file.catchupAndSubscribe((pointerField) => {
            this.#fileAdapter = pointerField.targetVertex.map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, AudioFileBoxAdapter));
            this.#fileSubscription.ifSome(subscription => subscription.terminate());
            this.#fileSubscription = this.#fileAdapter.map(adapter => adapter.getOrCreateLoader().subscribe(() => this.#dispatchChange()));
        }), this.#box.subscribe(Propagation.Children, (update) => {
            if (this.trackBoxAdapter.isEmpty()) {
                return;
            }
            if (update.type === "primitive" || update.type === "pointer") {
                const track = this.trackBoxAdapter.unwrap();
                if (this.#box.position.address.equals(update.address)) {
                    track.regions.onIndexingChanged();
                    this.#dispatchChange();
                }
                else {
                    this.#dispatchChange();
                }
            }
        }), {
            terminate: () => {
                this.#fileSubscription.ifSome(subscription => subscription.terminate());
                this.#fileSubscription = Option.None;
            }
        });
        this.#constructing = false;
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    accept(visitor) {
        return safeExecute(visitor.visitAudioRegionBoxAdapter, this);
    }
    onSelected() {
        this.#isSelected = true;
        this.#dispatchChange();
    }
    onDeselected() {
        this.#isSelected = false;
        this.#dispatchChange();
    }
    get isSelected() { return this.#isSelected; }
    terminate() { this.#terminator.terminate(); }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get position() { return this.#box.position.getValue(); }
    get duration() {
        const duration = this.#box.duration.getValue();
        if (duration === 0) { // signals no synchronization with track bpm
            const fileBoxAdapter = this.#fileAdapter.unwrap("Cannot compute duration without file");
            const startInSeconds = fileBoxAdapter.startInSeconds;
            const endInSeconds = fileBoxAdapter.endInSeconds;
            const totalInSeconds = endInSeconds - startInSeconds;
            return PPQN.secondsToPulses(totalInSeconds, this.#context.bpm);
        }
        return duration;
    }
    get complete() { return this.position + this.duration; }
    get loopOffset() { return this.#box.loopOffset.getValue(); }
    get loopDuration() { return this.#box.loopDuration.getValue(); }
    get offset() { return this.position - this.loopOffset; }
    get mute() { return this.#box.mute.getValue(); }
    get hue() { return this.#box.hue.getValue(); }
    get gain() { return this.#box.gain.getValue(); }
    get file() { return this.#fileAdapter.unwrap("Cannot access file."); }
    get hasCollection() { return this.#fileAdapter.nonEmpty(); }
    get optCollection() { return Option.None; }
    get label() {
        if (this.#fileAdapter.isEmpty()) {
            return "No Audio File";
        }
        const state = this.#fileAdapter.unwrap().getOrCreateLoader().state;
        if (state.type === "progress") {
            return `${Math.round(state.progress * 100)}%`;
        }
        if (state.type === "error") {
            return String(state.reason);
        }
        return this.#box.label.getValue();
    }
    get isMirrowed() { return false; }
    get canMirror() { return false; }
    get trackBoxAdapter() {
        return this.#box.regions.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, TrackBoxAdapter));
    }
    copyTo(params) {
        return this.#context.boxAdapters.adapterFor(AudioRegionBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(params?.position ?? this.position);
            box.duration.setValue(params?.duration ?? this.duration);
            box.loopOffset.setValue(params?.loopOffset ?? this.loopOffset);
            box.loopDuration.setValue(params?.loopDuration ?? this.loopDuration);
            box.regions.refer(params?.track ?? this.#box.regions.targetVertex.unwrap());
            box.file.refer(this.#box.file.targetVertex.unwrap());
            box.mute.setValue(this.mute);
            box.hue.setValue(this.hue);
            box.label.setValue(this.label);
            box.gain.setValue(this.gain);
        }), AudioRegionBoxAdapter);
    }
    consolidate() {
        // TODO This needs to done by creating a new audio file
    }
    canFlatten(_regions) { return false; }
    flatten(_regions) {
        // TODO This needs to done by creating a new audio file
        return Option.None;
    }
    toString() { return `{AudioRegionBoxAdapter ${UUID.toString(this.#box.address.uuid)}}`; }
    #dispatchChange() {
        if (this.#constructing) {
            return;
        }
        this.#changeNotifier.notify();
        this.trackBoxAdapter.unwrapOrNull()?.regions?.dispatchChange();
    }
}
